import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FileUploader } from 'ng2-file-upload';
import { CommonValidationService } from 'src/app/util/service/common-validation.service';
import { ImageUploadService, UploadImageAPIResponse } from 'src/app/util/service/image-upload.service';
import { LocalizationService, TranslationParams } from 'src/app/util/service/internationalization/localization.service';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { MapMarkerInfo } from '../../models/map-marker-info';

@Component({
    selector: 'app-map-marker-info-form',
    templateUrl: './map-marker-info-form.component.html',
    styleUrls: ['./map-marker-info-form.component.scss'],
    standalone: false
})
export class MapMarkerInfoFormComponent implements OnInit, OnChanges {
  @Input() token: string;
  @Input() markerInfoToBeEdited: MapMarkerInfo;

  @Output() formClosed: EventEmitter<MapMarkerInfo> = new EventEmitter<MapMarkerInfo>()
  @Output() formSaved: EventEmitter<MapMarkerInfo> = new EventEmitter<MapMarkerInfo>()
  @Output() markerRemoved: EventEmitter<string> = new EventEmitter<string>()

  isUploadedMarkerImageInvalid: boolean;
  markerInfoForm: UntypedFormGroup;
  temporaryUploadedMarkerImage: string | ArrayBuffer;
  uploader: FileUploader;

  constructor(private formBuilder: UntypedFormBuilder,
    private localizationService: LocalizationService,
    public translations: Translations,
    private uploadService: ImageUploadService,
    private validationService: CommonValidationService) { }

  ngOnInit() {
    this.createForm()
    this.uploader = this.uploadService.initializeFileUploader(this.token)
    this.patchFormIfMarkerIsToBeEdited()
  }

  ngOnChanges(): void {
      this.patchFormIfMarkerIsToBeEdited()
  }
  
  createForm() {
    this.markerInfoForm = this.formBuilder.group({
      latitude: [''],
      longitude: [''],
      infoImage: [''],
      mapInfo: ['', this.validationService.setCustomValidatorMethods({maxLength: 25, minLength: 2})],
      mapLink: ['', this.validationService.setCustomValidatorMethods({maxLength: 25, minLength: 2, disableRequired: true})]      
    })
  }

  closeForm() {
    this.formClosed.emit();
  }

  getTranslation(key: string, options?: TranslationParams) {
    return this.localizationService.getTranslation(key, options)
  }
  
  getValidationErrors(formControl: AbstractControl, formControlName: string, maxLength?: number, minLength?: number) {
    return this.validationService.getErrorMessage(formControl, formControlName, maxLength, minLength);
  }

  isUploadedHeroImageCdnImage() {
    return !this.temporaryUploadedMarkerImage
  }
  
  onImageSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target && target.files && target.files[0]) {
      if (this.validationService.isValidFileType(target.files[0].type)) {
        this.setFileUploaderValidationAndValues(target.files[0]);
      } else {
        this.resetFileUploaderValidationAndValues();
      }
    }
  }

  patchFormIfMarkerIsToBeEdited() {
    if(this.markerInfoToBeEdited && this.markerInfoForm) {
      this.markerInfoForm.patchValue(this.markerInfoToBeEdited)
    }
  }
  
  resetFileUploaderValidationAndValues() {
    if (this.isUploadedMarkerImageInvalid || this.infoImage.value) {
      this.temporaryUploadedMarkerImage = ""
      this.infoImage.setValue('');
    }
    this.isUploadedMarkerImageInvalid = true;
  }

  removeMarker() {
    this.markerRemoved.emit();
  }

  resetMarkerInfo(){
    this.mapInfo.reset()
    this.mapLink.reset()
    this.infoImage.reset()
    this.uploader.queue = []
    this.temporaryUploadedMarkerImage = null
  }

  saveForm(uploadedImages?: UploadImageAPIResponse[]) {
    if(uploadedImages && uploadedImages.length > 0 && uploadedImages[0] && uploadedImages[0].filename)
      this.infoImage.setValue(uploadedImages[0].filename)
    this.formSaved.emit(this.markerInfoForm.value)
  }

  setFileUploaderValidationAndValues(file: File) {
    this.isUploadedMarkerImageInvalid = false;
    const reader = new FileReader();
    reader.onload = () => {
      if(this.uploader.queue.length > 1)
        this.uploader.queue.splice(0, 1)
      this.temporaryUploadedMarkerImage = reader.result;
      this.infoImage.setValue(file.name);
    };
    reader.readAsDataURL(file);
  }

  submitForm(){
    this.markerInfoForm.markAllAsTouched();
    if(this.markerInfoForm.invalid)
      return
    if(this.uploadService.isImageUploaded(this.uploader)) {
      this.uploadService.uploadAllImages(this.uploader, this.saveForm, this)
    } else {
      if(this.markerInfoToBeEdited) {
        this.saveForm();
      } 
    }
  }

  get infoImage(): AbstractControl {
    return this.markerInfoForm.controls.infoImage
  }

  get mapInfo(): AbstractControl {
    return this.markerInfoForm.controls.mapInfo
  }

  get mapLink(): AbstractControl {
    return this.markerInfoForm.controls.mapLink
  }

}
